<template>
  <v-container
    id="extended-forms"
    fluid
    tag="section"
  >
    <validation-observer
      v-slot="{ handleSubmit }"
    >
      <form @submit.prevent="handleSubmit(submitForm)">
        <v-row>
          <v-col cols="12">
            <base-material-card
              class="pa-6"
              color="success"
              icon="mdi-account-key"
              :title="title"
            >
              <v-skeleton-loader
                class="mx-auto"
                max-width="300"
                type="card"
                :loading="loadingUser"
              >
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-row
                      align="center"
                      dense
                    >
                      <v-col cols="10">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required|max:100"
                          name="user.userName"
                        >
                          <v-text-field
                            v-model="user.userName"
                            :error-messages="errors"
                            color="secondary"
                            :label="$t('fields.user.userName')"
                            outlined
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <v-row
                      align="center"
                      dense
                    >
                      <v-col cols="10">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                          name="user.active"
                        >
                          <v-switch
                            v-model="user.active"
                            class="mx-2"
                            :label="$t('fields.user.active')"
                            :error-messages="errors"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-row
                      align="center"
                      dense
                    >
                      <v-col cols="10">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required|max:20"
                          name="user.password"
                        >
                          <v-text-field
                            v-model="user.password"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            :type="showPassword ? 'text' : 'password'"
                            :error-messages="errors"
                            color="secondary"
                            :label="$t('fields.user.password')"
                            outlined
                            @click:append="showPassword = !showPassword"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <v-row
                      align="center"
                      dense
                    >
                      <v-col cols="10">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                          name="user.roles"
                        >
                          <v-select
                            v-model="user.rolesId"
                            :items="roles"
                            item-text="name"
                            item-value="id"
                            :label="$t('fields.user.roles')"
                            :error-messages="errors"
                            outlined
                            multiple
                            chips
                            :loading="loadingRoles"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <div class="pa-3 text-center">
                  <v-btn
                    color="warning"
                    default
                    @click="goToList"
                  >
                    {{ $t('cancel') }}
                  </v-btn>
                  <v-btn
                    color="success"
                    default
                    type="submit"
                    :loading="loadingSave"
                  >
                    {{ $t("save") }}
                  </v-btn>
                </div>
              </v-skeleton-loader>
            </base-material-card>
          </v-col>
        </v-row>
      </form>
    </validation-observer>

    <base-material-snackbar
      v-model="showErrorMessage"
      type="error"
      v-bind="{
        ['top']: true,
        ['center']: true,
        ['vertical']: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import { repositoryFactory } from '@/api/repositoryFactory'
  const usersRespository = repositoryFactory.get('users')

  export default {
    inject: ['theme'],
    name: 'UserCrud',
    $_veeValidate: {
      validator: 'new',
    },
    data: () => ({
      user: {
        active: false,
      },
      roles: [],
      userStatuses: [],
      showErrorMessage: false,
      errorMessage: '',
      loadingUser: false,
      loadingRoles: false,
      loadingSave: false,
      formAction: null,
      showPassword: false,
    }),
    computed: {
      title () {
        return this.formAction === 'add' ? this.$t('add-user') : this.$t('modify-user')
      },
    },
    mounted () {
      var self = this
      self.formAction = this.$route.params.id === 'add' ? 'add' : 'modify'

      if (self.formAction !== 'add') {
        self.loadingUser = true
        var id = this.$route.params.id
        usersRespository.getById(id).then(response => {
          self.loadingUser = false
          self.user = Object.assign({}, response.data.data)
          self.user.rolesId = this.user.roles.map((x) => x.id)
        }).catch(err => {
          self.loadingUser = false
          alert(err)
        })
      }
      this.getRoles()
    },
    methods: {
      submitForm (scope) {
        var self = this
        var data = {
          data: this.user,
        }

        this.user.roles = this.user.rolesId.map((x) => {
          return {
            id: x,
            name: '',
          }
        })
        self.loadingSave = true
        if (self.formAction !== 'add') {
          usersRespository.updateUser(data, this.user.id).then(response => {
            if (response.data.isValid) {
              self.goToList()
            } else {
              self.showErrorMessage = true
              self.errorMessage = response.data.errorMessages[0]
            }
            self.loadingSave = false
          }).catch(err => {
            self.errorMessage = err
            self.showErrorMessage = true
            self.loadingSave = false
          })
        } else {
          usersRespository.addUser(data).then(response => {
            if (response.data.isValid) {
              self.goToList()
            } else {
              self.showErrorMessage = true
              self.errorMessage = response.data.errorMessages[0]
            }
            self.loadingSave = false
          }).catch(err => {
            self.errorMessage = err
            self.showErrorMessage = true
            self.loadingSave = false
          })
        }
      },
      getRoles () {
        var self = this
        self.loadingRoles = true
        usersRespository.getRoles().then(response => {
          self.roles = response.data.data
          self.loadingRoles = false
        }).catch(err => {
          self.loadingRoles = false
          alert(err)
        })
      },
      goToList () {
        this.$router.push({ name: 'users' })
      },
    },
  }
</script>
